import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CustomerHeader } from "@/components/customer/CustomerHeader";
import { ContactCard } from "@/components/customer/ContactCard";
import { AddOrderDialog } from "@/components/customer/AddOrderDialog";
import { MergeDialog } from "@/components/customer/MergeDialog";
import { OrderHistory } from "@/components/customer/OrderHistory";
import { DangerZone } from "@/components/customer/DangerZone";
import { ArrowLeft } from "lucide-react";

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [notes, setNotes] = useState("");
  const [categories, setCategories] = useState(["Other"]);
  const [orderDialog, setOrderDialog] = useState(false);
  const [mergeDialog, setMergeDialog] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers/${id}`);
      setCustomer(data.customer);
      setOrders(data.orders);
      setNotes(data.customer.notes || "");
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/settings/categories").then((r) => setCategories(r.data.values)).catch(() => {});
  }, []);

  if (!customer) return <p className="text-muted-foreground">Loading…</p>;

  const saveNotes = async () => {
    try {
      const { data } = await api.patch(`/customers/${id}`, { notes });
      setCustomer(data);
      toast.success("Notes saved.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const stats = [
    ["Lifetime total", fmtMoney(customer.lifetime_spend), "stat-lifetime-spend"],
    ["Orders", customer.order_count, "stat-order-count"],
    ["First seen", fmtDate(customer.first_seen), "stat-first-seen"],
    ["Last purchase", fmtDate(customer.last_purchase), "stat-last-purchase"],
  ];

  return (
    <div className="space-y-8" data-testid="customer-detail-page">
      <Link to="/customers" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-200" data-testid="back-to-customers-link">
        <ArrowLeft className="h-4 w-4" /> All customers
      </Link>

      <CustomerHeader
        customer={customer} customerId={id} isAdmin={isAdmin} onUpdated={setCustomer}
        onAddOrder={() => setOrderDialog(true)} onMerge={() => setMergeDialog(true)}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map(([label, value, tid]) => (
          <Card key={label} className="shadow-sm" data-testid={tid}>
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
              <p className="text-2xl font-serif font-semibold mt-1">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="space-y-8">
          <ContactCard customer={customer} customerId={id} onUpdated={setCustomer} />

          <Card className="shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-serif text-xl">Notes</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Anything worth remembering about this customer…" data-testid="notes-textarea" />
              <Button size="sm" variant="secondary" onClick={saveNotes} data-testid="save-notes-btn">Save notes</Button>
            </CardContent>
          </Card>

          {(customer.merge_history || []).length > 0 && (
            <Card className="shadow-sm" data-testid="merge-history-card">
              <CardHeader className="pb-3"><CardTitle className="font-serif text-xl">Merge history</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                {customer.merge_history.map((m) => (
                  <p key={`${m.merged_customer_id}-${m.at}`}>Absorbed <span className="text-foreground font-medium">{m.merged_name}</span> ({m.merged_email || "no email"}) on {fmtDate(m.at)} by {m.by}</p>
                ))}
              </CardContent>
            </Card>
          )}

          {isAdmin && (
            <DangerZone customer={customer} customerId={id} ordersCount={orders.length} onDeleted={() => navigate("/customers")} />
          )}
        </div>

        <div className="lg:col-span-2">
          <OrderHistory orders={orders} />
        </div>
      </div>

      <AddOrderDialog open={orderDialog} onOpenChange={setOrderDialog} customerId={id} categories={categories} onSaved={load} />
      <MergeDialog open={mergeDialog} onOpenChange={setMergeDialog} customer={customer} customerId={id} onMerged={load} />
    </div>
  );
}
